import React, { useEffect, useState } from "react";
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonReorder,
  IonReorderGroup,
} from "@ionic/react";
import clsx from "clsx";
import { removeCircleOutline as removeIcon } from "ionicons/icons";

import EmbeddedHeader from "../../../organisms/teacher/Header/EmbeddedHeader";
import { IClass, TeacherCategory } from "../../../state";
import ModalConfirm from "../../../molecules/ModalConfirm";
import CategoryForm from "../../../organisms/teacher/CategoryForm";

import styles from "./EditCategory.module.scss";

type Props = {
  propsCategories: TeacherCategory[];
  assignedClasses: IClass[];
  calling: boolean;
  updateCategory: (
    id: number,
    params: {
      name: string;
      class_ids: number[];
      color: string;
    },
  ) => void;
  deleteCategory: (id: number) => void;
  reorderCategories: (ids: number[]) => void;
};

const EditCategory = ({
  propsCategories,
  assignedClasses,
  calling,
  updateCategory,
  deleteCategory,
  reorderCategories,
}: Props) => {
  const [isEditable, setIsEditable] = useState(false);
  const [updateCategoryObj, setUpdateCategoryObj] = useState<
    Omit<TeacherCategory, "klasses"> & { class_ids: number[] }
  >();
  const [deleteCategoryId, setDeleteCategoryId] = useState<number | null>(null);
  const [categories, setCategories] = useState([...propsCategories]);

  const doReorder = (event: CustomEvent) => {
    const reordered: TeacherCategory[] = event.detail.complete([...categories]);
    reorderCategories(reordered.map(c => c.id));
    setCategories(reordered);
  };

  useEffect(() => {
    setCategories([...propsCategories]);
  }, [propsCategories]);

  return (
    <>
      <CategoryForm
        isOpen={!!updateCategoryObj}
        assignedClasses={assignedClasses}
        category={updateCategoryObj}
        calling={calling}
        onClose={() => setUpdateCategoryObj(undefined)}
        upsert={form => {
          !!updateCategoryObj && updateCategory(updateCategoryObj.id, form);
          setUpdateCategoryObj(undefined);
        }}
      />
      <ModalConfirm
        isOpen={!!deleteCategoryId}
        onClose={() => setDeleteCategoryId(null)}
        whiteMode
        message={`この活かし先を削除しますか？\nストックは削除されず、活かし先のみ削除されます。`}
        buttons={[
          {
            title: "キャンセル",
            type: "None",
            action: () => {
              setDeleteCategoryId(null);
            },
          },
          {
            title: "削除する",
            type: "Danger",
            action: () => {
              !!deleteCategoryId && deleteCategory(deleteCategoryId);
              setDeleteCategoryId(null);
            },
          },
        ]}
      />
      <EmbeddedHeader
        title="活かし先を編集"
        rightComponent={
          <IonButton
            className={styles.editButton}
            fill="clear"
            disabled={calling}
            onClick={() => setIsEditable(!isEditable)}
          >
            <p className={styles.editButtonTitle}>
              {isEditable ? "完了" : "編集"}
            </p>
          </IonButton>
        }
      />
      <IonReorderGroup
        disabled={!isEditable || calling}
        onIonItemReorder={doReorder}
      >
        {categories.map((category, i) => {
          const cssDeclarationColor = {
            background: category.color,
          };
          const classLabel = category.klasses
            ?.map(cls => cls.full_name)
            .join(", ");
          return (
            <IonItem
              key={category.id}
              className={clsx(
                styles.categoryItem,
                isEditable && !calling && styles.pointer,
              )}
              lines={i + 1 === categories.length ? "none" : "inset"}
              onClick={() => {
                isEditable &&
                  !calling &&
                  setUpdateCategoryObj({
                    id: category.id,
                    name: category.name,
                    class_ids: category.klasses.map(c => c.id),
                    color: category.color,
                  });
              }}
            >
              {isEditable && (
                <IonButton
                  className={styles.removeButton}
                  disabled={calling}
                  slot="start"
                  fill="clear"
                  size="large"
                  onClick={e => {
                    e.stopPropagation();
                    setDeleteCategoryId(category.id);
                  }}
                >
                  <IonIcon icon={removeIcon} />
                </IonButton>
              )}
              <div className={styles.colorTip} style={cssDeclarationColor} />
              <IonLabel className={styles.categoryName}>
                {category.name}
              </IonLabel>
              <IonLabel slot="end" className={styles.classLabel}>
                {classLabel}
              </IonLabel>
              <IonReorder slot="end" className={styles.reorderIcon} />
            </IonItem>
          );
        })}
      </IonReorderGroup>
    </>
  );
};

export default EditCategory;
